import React, { useMemo, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PitchBreakPlotChart, pitchTypeDisplayName, pitchSubTypeColor, mapPlaylistFromVideoSearch, pitchTypeColor } from 'yankees-react-utils';
import { useOktaAuth } from '@okta/okta-react';
import { Video } from '../../../../data';
import useConfig from '../../../../utils/useConfig';
import { VideoPlayerModal } from '../../../../components/VideoPlayer';
import { store } from '../../../../store';

const getPitchCharSeries = (pitches, pitchCharacteristics, throws, pitcherId, yearId, currentYear, repertoire, translate) => {
    let pcuse = pitchCharacteristics.pitches;
    let applyYearFilter = false;
    if (throws) {
        pcuse = pcuse.filter((x) => x.pitcherid === throws); // prefer the pitches averaged for their arm type
    } else {
        pcuse = pcuse.filter((x) => x.pitcherid !== pitcherId); // backup is things that aren't the specific player
        applyYearFilter = true; // in these cases we need to filter out some old year data that could be in the set (below)
    }
    if (applyYearFilter && yearId === -1) {
        pcuse = pcuse.filter((y) => y.yearid === currentYear || y.yearid === currentYear - 1);
    } else if (applyYearFilter) {
        pcuse = pcuse.filter((y) => y.yearid === yearId);
    }
    const pitchtypes = new Set(pitches.map((x) => x.fxtype));
    pcuse = pcuse.filter((y) => pitchtypes.has(y.pitchtype));
    const pitchCharSeries = [];
    pcuse.forEach((pitch) => {
        const movementNote = pitchCharacteristics?.movementnotes?.find((x) => x.pitchtype === pitch.pitchtype);
        pitchCharSeries.push({
            // eslint-disable-next-line no-nested-ternary
            name: `${throws === 1 ? translate('baseball.rhp') : throws === 2 ? translate('baseball.lhp') : ''} 
            ${pitchTypeDisplayName({ pitchtype: pitch.pitchtype }, false, false, translate)} ${translate('sections.opponent.player.league-average')}`,
            color: pitchTypeColor(pitch.pitchtype),
            tooltip: { pointFormat: `${translate('baseball.break-horiz')}: {point.breakx54}<br />${translate('baseball.break-vert')}: {point.breakz54}` },
            data: [{
                yearid: pitch.yearid,
                x: pitch.breakx54,
                y: pitch.breakz54,
                diffx: movementNote?.diffx,
                diffz: movementNote?.diffz,
                breakx54: pitch.breakx54.toFixed(2),
                breakz54: pitch.breakz54.toFixed(2),
                marker: {
                    lineColor: 'rgba(0, 0, 0, .8)',
                    fillColor: pitchTypeColor(pitch.pitchtype),
                    lineWidth: 2,
                    radius: 6,
                },
            }],
            zIndex: 99,
            showInLegend: false,
        });
    });

    return pitchCharSeries;
};
const PitchBreakChart = ({ pitches, pitchCharacteristics, throws, pitcherId, yearId, repertoire }) => {
    const { state: { user } } = useContext(store);
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();
    const { authState: { accessToken } } = useOktaAuth();

    const modalContainer = useRef(null);

    const plotLines = [...Array(18).keys()].map((i) => ({
        color: 'lightgrey',
        value: (i - 9) * 3,
        width: 1,
    }));
    const pitchVideoFn = window.$IsDugoutApp ? null : async (gameid, eventseq, pitchseq) => {
        const resp = await Video.search(accessToken, { gameid, eventseq, pitchseq });
        return mapPlaylistFromVideoSearch(resp, user.Roles.map((r) => r.RoleName));
    };
    const pitchCharSeries = getPitchCharSeries(pitches, pitchCharacteristics, throws, pitcherId, yearId, CurrentYear, repertoire, translate);
    const sortedPitches = useMemo(() => pitches.filter((p) => p.fxtype !== 1).map((p) => ({ ...p, pitchtype: p.fxtype }))
        .sort((a, b) => repertoire.findIndex((r) => r.fxtype === a.fxtype && r.fxsubtype === a.fxsubtype)
         - repertoire.findIndex((r) => r.fxtype === b.fxtype && r.fxsubtype === b.fxsubtype)), [pitches, repertoire]);

    return (
        <div ref={modalContainer}>
            <PitchBreakPlotChart
                enableZoom={false}
                pitches={sortedPitches}
                seriesData={pitchCharSeries}
                showlegend
                legendposition="below"
                width={320}
                height={385}
                keyX="breakx54"
                keyY="breakz54"
                perspective="catcher"
                plotLinesX={plotLines}
                plotLinesY={plotLines}
                abbreviateName
                colorFunc={(pt) => pitchSubTypeColor(pt)}
                pitchVideoFn={pitchVideoFn}
                videoPlayerModal={VideoPlayerModal}
                modalContainer={modalContainer}
            />
        </div>
    );
};

export default PitchBreakChart;
